import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { ClipboardList, MapPin, Clock, ArrowRight, Truck, MessageSquare, Search, Edit3, FileBadge2, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import LoadChatDrawer from '../components/LoadChatDrawer';
import LoadEditModal from '../components/LoadEditModal';
import LoadDocumentsModal from '../components/LoadDocumentsModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

import { LOAD_STATUS_COLORS as STATUS_COLORS, LOAD_STATUS_LABELS as STATUS_LABELS } from '../lib/loadStatus';

const DispatcherDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loads, setLoads] = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active');
  const [query, setQuery] = useState('');
  const [chatLoad, setChatLoad] = useState(null);
  const [editLoad, setEditLoad] = useState(null);
  const [docsLoad, setDocsLoad] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [loadsRes, driversRes] = await Promise.all([
        axios.get(`${API}/loads`, { withCredentials: true }),
        axios.get(`${API}/drivers`, { withCredentials: true }),
      ]);
      setLoads(loadsRes.data);
      setDrivers(driversRes.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getDriverName = (driverId) => {
    const d = drivers.find((x) => x.user_id === driverId);
    return d ? d.name : '-';
  };

  const counts = useMemo(() => ({
    all: loads.length,
    active: loads.filter(l => !['delivered', 'cancelled', 'rejected'].includes(l.status)).length,
    in_transit: loads.filter(l => l.status === 'in_transit').length,
    assigned: loads.filter(l => l.status === 'assigned').length,
    delivered: loads.filter(l => l.status === 'delivered').length,
  }), [loads]);

  const filtered = useMemo(() => {
    let list = loads;
    if (filter === 'active') list = list.filter(l => !['delivered', 'cancelled', 'rejected'].includes(l.status));
    else if (filter !== 'all') list = list.filter(l => l.status === filter);
    if (query) {
      const q = query.toLowerCase();
      list = list.filter(l => {
        const hay = `${l.load_ref || ''} ${l.pickup_location || ''} ${l.delivery_location || ''} ${l.cargo_description || ''} ${l.broker || ''} ${getDriverName(l.driver_id) || ''}`.toLowerCase();
        return hay.includes(q);
      });
    }
    return list.sort((a, b) => new Date(b.updated_at || b.created_at) - new Date(a.updated_at || a.created_at));
  }, [loads, filter, query, drivers]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <DashboardLayout><div className="p-8" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Loading...</div></DashboardLayout>;
  }

  const activeDrivers = drivers.filter((d) => d.status === 'active').length;

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8 max-w-7xl mx-auto" data-testid="dispatcher-dashboard">
        <div className="mb-8 flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-bold tracking-tight text-slate-900 mb-2" style={{ fontFamily: 'Work Sans, sans-serif' }}>Dispatcher Control Room</h1>
            <p className="text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Monitor and manage all loads and routes</p>
          </div>
          <button
            data-testid="manage-loads-button"
            onClick={() => navigate('/loads')}
            className="flex items-center gap-2 bg-[#002FA7] text-white px-4 py-2 rounded-md font-semibold hover:bg-[#00247D] transition-all duration-200"
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            New Load
            <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard testid="stat-card-active-loads" icon={ClipboardList} color="text-[#002FA7]" label="Active Loads" value={counts.active} />
          <StatCard testid="stat-card-in-transit" icon={MapPin} color="text-[#34C759]" label="In Transit" value={counts.in_transit} />
          <StatCard testid="stat-card-pending" icon={Clock} color="text-[#FF9500]" label="Awaiting Pickup" value={counts.assigned} />
          <StatCard testid="stat-card-drivers" icon={Truck} color="text-[#002FA7]" label="Active Drivers" value={activeDrivers} />
        </div>

        <div className="bg-white border border-slate-200 rounded-md">
          <div className="px-5 py-4 border-b border-slate-200 flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-xl font-semibold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>All Loads</h2>
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="search-loads"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search loads, drivers, brokers..."
                className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-200 text-sm focus:outline-none focus:border-[#002FA7]"
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              />
            </div>
          </div>

          <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2">
            {[
              { id: 'active', label: 'Active' },
              { id: 'assigned', label: 'Assigned' },
              { id: 'in_transit', label: 'In Transit' },
              { id: 'delivered', label: 'Delivered' },
              { id: 'all', label: 'All' },
            ].map(f => (
              <button
                key={f.id}
                data-testid={`filter-${f.id}`}
                onClick={() => setFilter(f.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-all ${filter === f.id ? 'bg-[#002FA7] text-white border-[#002FA7]' : 'bg-white border-slate-200 text-slate-700 hover:border-slate-400'}`}
                style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
              >
                {f.label} <span className="ml-1 opacity-70">({counts[f.id]})</span>
              </button>
            ))}
          </div>

          {filtered.length === 0 ? (
            <p className="p-8 text-sm text-slate-500 text-center" data-testid="no-loads-message" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {loads.length === 0 ? 'No loads yet. Create one to get started.' : 'No loads match your filters.'}
            </p>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="md:hidden divide-y divide-slate-100" data-testid="dispatcher-loads-mobile-list">
                {filtered.map((load) => (
                  <div key={load.id} data-testid={`mobile-live-load-${load.id}`} className="p-4">
                    <div className="flex items-start justify-between gap-3 mb-2">
                      <div className="min-w-0">
                        <p className="text-[10px] font-mono text-slate-500 uppercase tracking-widest" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                          {load.load_ref || `#${load.id.slice(-6)}`}
                        </p>
                        <p className="text-sm font-semibold text-slate-900 truncate mt-0.5" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                          {getDriverName(load.driver_id)}
                        </p>
                      </div>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-semibold uppercase tracking-wider whitespace-nowrap ${STATUS_COLORS[load.status]}`}>
                        {STATUS_LABELS[load.status]}
                      </span>
                    </div>
                    <p className="text-xs text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <span className="text-slate-400">Route:</span> {load.pickup_location}
                      <span className="mx-1 text-slate-400">→</span>
                      {load.delivery_location}
                    </p>
                    <p className="text-xs text-slate-600 mb-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      <span className="text-slate-400">Cargo:</span> {load.cargo_description || '—'}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setChatLoad({ id: load.id, load_ref: load.load_ref || load.id.slice(-6) })}
                        data-testid={`mobile-chat-load-${load.id}`}
                        className="flex-1 min-h-[40px] flex items-center justify-center gap-1.5 text-xs font-semibold text-[#002FA7] bg-[#002FA7]/5 active:bg-[#002FA7]/10 rounded-md"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        <MessageSquare className="w-4 h-4" /> Chat
                      </button>
                      <button
                        onClick={() => setDocsLoad(load)}
                        data-testid={`mobile-docs-load-${load.id}`}
                        className="flex-1 min-h-[40px] flex items-center justify-center gap-1.5 text-xs font-semibold text-slate-700 bg-slate-100 active:bg-slate-200 rounded-md"
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        <FileBadge2 className="w-4 h-4" /> Docs
                      </button>
                      <button
                        onClick={() => setEditLoad(load)}
                        data-testid={`mobile-edit-load-${load.id}`}
                        className={`flex-1 min-h-[40px] flex items-center justify-center gap-1.5 text-xs font-semibold rounded-md ${load.status === 'delivered' ? 'text-[#FF6B00] bg-orange-50 active:bg-orange-100' : 'text-white bg-[#002FA7] active:bg-[#00247D]'}`}
                        style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                      >
                        {load.status === 'delivered' ? <RefreshCw className="w-4 h-4" /> : <Edit3 className="w-4 h-4" />}
                        {load.status === 'delivered' ? 'Reopen' : 'Edit'}
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="overflow-x-auto hidden md:block">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Ref</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Driver</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Pickup → Delivery</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Cargo</th>
                    <th className="text-left py-2.5 px-4 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Status</th>
                    <th className="text-right py-2.5 px-4 text-xs font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((load) => (
                    <tr key={load.id} data-testid={`live-load-${load.id}`} className="border-b border-slate-100 hover:bg-slate-50 transition-colors">
                      <td className="py-3 px-4 text-sm font-mono text-slate-700" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{load.load_ref || `#${load.id.slice(-6)}`}</td>
                      <td className="py-3 px-4 text-sm text-slate-900 font-medium" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{getDriverName(load.driver_id)}</td>
                      <td className="py-3 px-4 text-sm text-slate-600" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                        <span className="truncate">{load.pickup_location}</span>
                        <span className="text-slate-400 mx-1.5">→</span>
                        <span className="truncate">{load.delivery_location}</span>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600 truncate max-w-[200px]" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{load.cargo_description || '-'}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${STATUS_COLORS[load.status]}`}>
                          {STATUS_LABELS[load.status]}
                        </span>
                      </td>
                      <td className="py-3 px-4 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button
                            data-testid={`chat-load-${load.id}`}
                            onClick={() => setChatLoad({ id: load.id, load_ref: load.load_ref || load.id.slice(-6) })}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold text-[#002FA7] hover:bg-[#002FA7]/10 transition-all"
                            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                            title="Open chat"
                          >
                            <MessageSquare className="w-3.5 h-3.5" />
                            Chat
                          </button>
                          <button
                            data-testid={`docs-load-${load.id}`}
                            onClick={() => setDocsLoad(load)}
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold text-slate-600 hover:bg-slate-100 hover:text-[#002FA7] transition-all"
                            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                            title="Documents"
                          >
                            <FileBadge2 className="w-3.5 h-3.5" />
                            Docs
                          </button>
                          <button
                            data-testid={`edit-load-${load.id}`}
                            onClick={() => setEditLoad(load)}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-md text-xs font-semibold transition-all ${load.status === 'delivered' ? 'text-[#FF6B00] hover:bg-orange-50' : 'text-slate-600 hover:bg-slate-100 hover:text-[#002FA7]'}`}
                            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                            title={load.status === 'delivered' ? 'Reopen / correct' : 'Edit status / reassign'}
                          >
                            {load.status === 'delivered' ? <RefreshCw className="w-3.5 h-3.5" /> : <Edit3 className="w-3.5 h-3.5" />}
                            {load.status === 'delivered' ? 'Reopen' : 'Edit'}
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            </>
          )}
        </div>
      </div>

      <LoadChatDrawer
        loadId={chatLoad?.id}
        loadRef={chatLoad?.load_ref}
        open={!!chatLoad}
        onClose={() => setChatLoad(null)}
      />
      <LoadEditModal
        load={editLoad}
        drivers={drivers}
        role={user?.role}
        open={!!editLoad}
        onClose={() => setEditLoad(null)}
        onUpdated={fetchData}
      />
      <LoadDocumentsModal
        load={docsLoad}
        role={user?.role}
        currentUserId={user?.id}
        open={!!docsLoad}
        onClose={() => setDocsLoad(null)}
      />
    </DashboardLayout>
  );
};

const StatCard = ({ testid, icon: Icon, color, label, value }) => (
  <div data-testid={testid} className="bg-white border border-slate-200 rounded-md p-5">
    <div className="flex items-center gap-3">
      <Icon className={`w-7 h-7 ${color}`} strokeWidth={2} />
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>{label}</p>
        <p className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>{value}</p>
      </div>
    </div>
  </div>
);

export default DispatcherDashboard;
